// FormControl. Untouched until #4366, so helper text was rendering as Chakra stock `gray.500` at `fontSize: sm`.
export const Form = {
  parts: ["container", "requiredIndicator", "helperText"],
  baseStyle: {
    helperText: {
      mt: "8px",
      px: "12px",
      fontFamily: "Larsseit",
      fontFeatureSettings: '"ss01" on, "ss02" on',
      fontSize: "12px",
      lineHeight: 1.4,
      letterSpacing: "-0.02em",
      color: "text.secondary",
      // Pairs with `data-warning` on the field. Chakra has no warning state of its own.
      "&[data-warning]": {
        color: "system.warning",
      },
    },
  },
  variants: {
    // Floating label, per the Figma component set. Opt in per call site with `<FormControl variant="floating">`, and
    // render the label anywhere inside the control — position is handled here, not by DOM order.
    //
    // Driven by CSS rather than React state on purpose: 14 of the fields being migrated have no `value` prop at all,
    // and several put `onChange` on the FormControl rather than the input, so a JS `focus || value` check never floats
    // the label on autofill or on a ref-populated field.
    //
    // Two requirements at the call site:
    //  1. every floating field needs a placeholder attribute, `placeholder=" "` if there is nothing to show, otherwise
    //     `:placeholder-shown` never stops matching and the label never floats;
    //  2. `type="date"` never matches `:placeholder-shown`, so those fields set `data-float` on the FormControl once
    //     they hold a value.
    floating: {
      container: {
        pos: "relative",
        label: {
          pos: "absolute",
          insetStart: 0,
          top: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          h: "48px",
          px: "12px",
          m: 0,
          fontFamily: "Larsseit",
          fontFeatureSettings: '"ss01" on, "ss02" on',
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: 1.4,
          letterSpacing: "-0.02em",
          color: "text.placeholder",
          transformOrigin: "left center",
          transition: "transform 0.2s ease, font-size 0.2s ease, line-height 0.2s ease, letter-spacing 0.2s ease, color 0.2s ease",
          pointerEvents: "none",
        },
        // Only a field with a label needs room for it. Without one the value stays vertically centred, i.e. a plain input.
        "&:has(label) input, &:has(label) textarea": {
          pt: "20px",
          pb: "8px",
        },
        // At rest the label sits where the placeholder would, so the placeholder only shows once the field is active.
        "&:has(label):not([data-float]) input:not(:focus)::placeholder, &:has(label):not([data-float]) textarea:not(:focus)::placeholder": {
          color: "transparent",
        },
        // Scoped to the field itself rather than `:focus-within`: trailing addons (the password reveal button) live
        // inside the same FormControl, so `:focus-within` floated the label when the button was clicked on an empty
        // field.
        "&:has(input:focus) label, &:has(textarea:focus) label, &[data-float] label, &:has(input:not(:placeholder-shown)) label, &:has(textarea:not(:placeholder-shown)) label":
          {
            // Box geometry stays put — same 48px flex box, same `top` — so the text stays centred while it shrinks and
            // only interpolatable properties change. `height: auto` plus a `top` change did not animate: the box
            // collapsed instantly and the label appeared at the top, then slid *down* to 8px.
            //
            // Resting text centre sits at 24px (48 / 2). The design's floated label is 10px/1.2 at top 8px, so its text
            // centre is at 14px. Hence the -10px travel.
            //
            // Known limitation: the 48px box assumes a 48px field. The selectors also match `textarea`, which is
            // taller, so a floating textarea would centre its label against the wrong height. No call site does that.
            transform: "translateY(-10px)",
            fontSize: "10px",
            lineHeight: 1.2,
            letterSpacing: "normal",
          },
        // FormControl stamps data-disabled onto the label itself (form-control.tsx getLabelProps), so the disabled and
        // invalid label colours come free without a prop at the call site.
        "label[data-disabled]": {
          color: "text.disabled",
        },
      },
    },
  },
}
